// File: src/pages/ReadingRoom.jsx
// Description: Enhanced Reading Room page with backend API integration
// This component fetches real data from the Mahavihara API and displays books, magazines, and articles
// with advanced filtering, sorting, and search capabilities

import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import ResourceCard from '../components/ReadingRoom/ResourceCard';
import PDFReader from '../components/ReadingRoom/PDFReader';
import { 
  BookOpenIcon, 
  DocumentTextIcon, 
  NewspaperIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// ============================================================================
// API SERVICE FUNCTIONS
// ============================================================================

const API_BASE = 'https://api.mahavihara-international.org';

/**
 * Fetches all reading room resources from the backend
 * @returns {Promise<Array>} Array of reading room items
 */
const getReadingRoomResources = async () => {
  try {
    const response = await fetch(`${API_BASE}/reading-room`);
    const result = await response.json();
    
    if (result.success) {
      return result.data || [];
    } else {
      throw new Error(result.message || 'Failed to fetch resources');
    }
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

/**
 * Converts backend file path to full URL
 * @param {string} path - Relative file path from API (e.g., "uploads/reading-room/files/book.pdf")
 * @returns {string|null} Full URL or null if path is empty
 */
const getFileUrl = (path) => {
  return path ? `${API_BASE}/${path}` : null;
};

/**
 * Detects file format from file path
 * @param {string} filePath - File path or URL
 * @returns {string} File format (PDF, DOC, DOCX, etc.)
 */
const detectFileFormat = (filePath) => {
  if (!filePath) return 'PDF'; // Default format
  
  const extension = filePath.split('.').pop().toUpperCase();
  return extension || 'PDF';
};

/**
 * Estimates file size category based on format (since API doesn't provide size)
 * @param {string} format - File format
 * @returns {string} Estimated size range
 */
const estimateFileSize = (format) => {
  // This is a placeholder - in production, you might want to fetch actual file sizes
  const sizeMap = {
    'PDF': '2-5 MB',
    'DOC': '1-3 MB',
    'DOCX': '1-3 MB',
    'default': '2-4 MB'
  };
  return sizeMap[format] || sizeMap.default;
};

/**
 * Attempts to categorize resource based on title/author keywords
 * This is a temporary solution until backend provides category field
 * @param {string} titleEn - English title
 * @param {string} authorEn - English author
 * @returns {string} Category key
 */
const detectCategory = (titleEn, authorEn) => {
  const title = (titleEn || '').toLowerCase();
  const author = (authorEn || '').toLowerCase();
  
  // Check for magazine/newsletter keywords
  if (title.includes('magazine') || title.includes('newsletter') || 
      title.includes('journal') || title.includes('bulletin')) {
    return 'magazines';
  }
  
  // Check for article keywords
  if (title.includes('article') || title.includes('research') || 
      title.includes('study') || title.includes('paper')) {
    return 'articles';
  }
  
  // Check for Sayadaw/Venerable in author
  if (author.includes('sayadaw') || author.includes('venerable') || 
      author.includes('bhikkhu') || author.includes('monk')) {
    return 'books_sayadaws';
  }
  
  // Check for academic titles (Prof, Dr, etc.)
  if (author.includes('prof') || author.includes('dr.') || 
      author.includes('doctor') || author.includes('lecturer')) {
    return 'books_lecturers';
  }
  
  // Default to books by sayadaws
  return 'books_sayadaws';
};

/**
 * Estimates difficulty level based on title keywords
 * This is a temporary solution until backend provides difficulty field
 * @param {string} titleEn - English title
 * @returns {string} Difficulty level (beginner, intermediate, advanced)
 */
const detectDifficulty = (titleEn) => {
  const title = (titleEn || '').toLowerCase();
  
  if (title.includes('introduction') || title.includes('beginner') || 
      title.includes('basic') || title.includes('guide')) {
    return 'beginner';
  }
  
  if (title.includes('advanced') || title.includes('comprehensive') || 
      title.includes('in-depth') || title.includes('scholarly')) {
    return 'advanced';
  }
  
  return 'intermediate';
};

/**
 * Extracts tags from title and description
 * @param {string} titleEn - English title
 * @param {string} descEn - English description
 * @returns {Array<string>} Array of tags
 */
const extractTags = (titleEn, descEn) => {
  const text = `${titleEn} ${descEn}`.toLowerCase();
  const tags = [];
  
  // Common Buddhist/academic keywords
  const keywords = [
    'meditation', 'vipassana', 'dhamma', 'buddhism', 'pali',
    'philosophy', 'ethics', 'mindfulness', 'wisdom', 'enlightenment',
    'research', 'study', 'analysis', 'teaching', 'practice'
  ];
  
  keywords.forEach(keyword => {
    if (text.includes(keyword)) {
      tags.push(keyword);
    }
  });
  
  return tags.slice(0, 5); // Limit to 5 tags
};

// ============================================================================
// CATEGORY DEFINITIONS
// ============================================================================

const CATEGORIES = {
  books_sayadaws: {
    en: 'Books by Venerable Sayadaws',
    my: 'ဆရာတော်များ ရေးသားသော ကျမ်းများ',
    icon: BookOpenIcon,
    color: 'text-primary-blue',
    description: { en: 'Spiritual teachings and wisdom', my: 'ဝိညာဏ်ရေး ဆုံးမများနှင့် ပညာတရား' }
  },
  magazines: {
    en: 'Magazines & Newsletters',
    my: 'မဂ္ဂဇင်းများနှင့် သတင်းလွှာများ',
    icon: NewspaperIcon,
    color: 'text-primary-gold',
    description: { en: 'Periodic publications and updates', my: 'အချိန်အပိုင်းအခြား ထုတ်ဝေမှုများနှင့် သတင်းများ' }
  },
  articles: {
    en: 'Research Articles',
    my: 'သုတေသန ဆောင်းပါးများ',
    icon: DocumentTextIcon,
    color: 'text-primary-green',
    description: { en: 'Academic research and analysis', my: 'ပညာရေး သုတေသနနှင့် ခွဲခြမ်းစိတ်ဖြာမှုများ' }
  },
  books_lecturers: {
    en: 'Books by Faculty',
    my: 'ကထိကများ ရေးသားသော ကျမ်းများ',
    icon: BookOpenIcon,
    color: 'text-primary-red',
    description: { en: 'Academic textbooks and guides', my: 'ပညာရေး ဖတ်စာများနှင့် လမ်းညွှန်များ' }
  }
};

// ============================================================================
// SORT OPTIONS
// ============================================================================

const SORT_OPTIONS = {
  date_desc: { en: 'Newest First', my: 'အသစ်ဆုံးများ အရင်' },
  date_asc: { en: 'Oldest First', my: 'အဟောင်းဆုံးများ အရင်' },
  title_asc: { en: 'Title A-Z', my: 'ခေါင်းစဉ် က-အ' },
  title_desc: { en: 'Title Z-A', my: 'ခေါင်းစဉ် အ-က' },
  author_asc: { en: 'Author A-Z', my: 'စာရေးသူ က-အ' }
};

// ============================================================================
// DIFFICULTY LEVELS
// ============================================================================

const DIFFICULTY_LEVELS = {
  beginner: { en: 'Beginner', my: 'အစပြု' },
  intermediate: { en: 'Intermediate', my: 'အလယ်အလတ်' },
  advanced: { en: 'Advanced', my: 'အဆင့်မြင့်' }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ReadingRoom = () => {
  // ============================================================================
  // HOOKS & STATE
  // ============================================================================
  
  const { currentLanguage, getText, isLoading: languageLoading } = useLanguage();
  
  // Resources state - stores all fetched resources from API
  const [resources, setResources] = useState([]);
  
  // Filtered resources after applying search and filters
  const [filteredResources, setFilteredResources] = useState([]);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  
  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showReader, setShowReader] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [error, setError] = useState(null);

  // ============================================================================
  // DATA FETCHING - Fetch resources from API on component mount
  // ============================================================================
  
  useEffect(() => {
    const fetchResources = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch data from API
        const apiData = await getReadingRoomResources();
        
        // Transform API data to match frontend structure
        const mappedResources = apiData.map(item => {
          const format = detectFileFormat(item.file_path);
          const fileUrl = item.file_path ? getFileUrl(item.file_path) : null;
          const category = detectCategory(item.title_en, item.author_en);
          const difficulty = detectDifficulty(item.title_en);
          const tags = extractTags(item.title_en, item.header_text_en);
          
          return {
            // Basic info
            id: item.id,
            
            // Bilingual title
            title: {
              en: item.title_en || 'Untitled',
              my: item.title_mm || 'ခေါင်းစဉ်မရှိ'
            },
            
            // Bilingual author
            author: {
              en: item.author_en || 'Unknown Author',
              my: item.author_mm || 'စာရေးသူ အမည်မသိ'
            },
            
            // Bilingual description (using header_text as description)
            description: {
              en: item.header_text_en || 'No description available.',
              my: item.header_text_mm || 'ဖော်ပြချက် မရှိပါ။'
            },
            
            // Category (auto-detected from title/author)
            category: category,
            
            // File information
            coverImage: getFileUrl(item.cover_image),
            downloadUrl: fileUrl,
            readUrl: fileUrl,
            format: format,
            fileSize: estimateFileSize(format),
            
            // Metadata
            publishDate: item.created_at || new Date().toISOString(),
            language: 'both', // Assuming all resources are bilingual
            difficulty: difficulty,
            tags: tags
          };
        });
        
        setResources(mappedResources);
        setFilteredResources(mappedResources);
        
      } catch (err) {
        console.error('Failed to load reading room resources:', err);
        setError(err.message || 'Failed to load resources. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchResources();
  }, []); // Empty dependency array - fetch only once on mount

  // ============================================================================
  // FILTERING & SORTING - Process resources based on filters and sort options
  // ============================================================================
  
  const processedResources = useMemo(() => {
    let filtered = [...resources];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(resource => resource.category === selectedCategory);
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(resource => resource.difficulty === selectedDifficulty);
    }

    // Filter by search query (searches in title, author, description, and tags)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(resource => 
        getText(resource.title).toLowerCase().includes(query) ||
        getText(resource.author).toLowerCase().includes(query) ||
        (resource.description && getText(resource.description).toLowerCase().includes(query)) ||
        (resource.tags && resource.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Sort resources based on selected sort option
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.publishDate) - new Date(a.publishDate);
        case 'date_asc':
          return new Date(a.publishDate) - new Date(b.publishDate);
        case 'title_asc':
          return getText(a.title).localeCompare(getText(b.title));
        case 'title_desc':
          return getText(b.title).localeCompare(getText(a.title));
        case 'author_asc':
          return getText(a.author).localeCompare(getText(b.author));
        default:
          return 0;
      }
    });

    return filtered;
  }, [resources, selectedCategory, selectedDifficulty, searchQuery, sortBy, currentLanguage, getText]);

  // ============================================================================
  // PAGINATION - Calculate paginated resources
  // ============================================================================
  
  const totalPages = Math.ceil(processedResources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResources = processedResources.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedDifficulty, searchQuery, sortBy]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  /**
   * Opens the PDF reader modal with selected resource
   */
  const handleReadOnline = (resource) => {
    if (!resource.readUrl) {
      alert(getText({
        en: 'This resource does not have a readable file.',
        my: 'ဤအရင်းအမြစ်တွင် ဖတ်ရှုနိုင်သော ဖိုင် မရှိပါ။'
      }));
      return;
    }
  
    // ✅ Open PDF in new browser tab (Edge-safe)
    window.open(resource.readUrl, '_blank', 'noopener,noreferrer');
  };
  /**
   * Downloads the selected resource file
   */
  const handleDownload = async (resource) => {
    if (!resource.downloadUrl) {
      alert(getText({
        en: 'This resource does not have a downloadable file.',
        my: 'ဤအရင်းအမြစ်တွင် ဒေါင်းလုဒ်လုပ်နိုင်သော ဖိုင် မရှိပါ။'
      }));
      return;
    }

    const link = document.createElement('a');
    link.href = resource.downloadUrl;
    link.download = `${getText(resource.title)}.${resource.format.toLowerCase()}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Calculates statistics for each category
   */
  const getCategoryStats = () => {
    const stats = {};
    Object.keys(CATEGORIES).forEach(category => {
      stats[category] = resources.filter(r => r.category === category).length;
    });
    stats.all = resources.length;
    return stats;
  };

  /**
   * Clears all active filters and resets to defaults
   */
  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setSearchQuery('');
    setSortBy('date_desc');
  };

  const categoryStats = getCategoryStats();

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  
  if (languageLoading) {
    return (
      <div className="reading-room">
        <div className="loading-fallback">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
            </div>
            <p className="loading-message">
              {getText({
                en: 'Loading...',
                my: 'ဖွင့်နေသည်...'
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className="reading-room">
      {/* Main Content */}
      <section className="main-content">
        <div className="container">
          
          {/* ============================================================================
              SEARCH AND FILTER BAR
              ============================================================================ */}
          
          <div className="search-filter-section">
           

            {/* Filter Controls */}
            <div className="filter-controls">
              {/* Toggle Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              >
                <FunnelIcon className="filter-icon" />
                {getText({
                  en: 'Filters',
                  my: 'စစ်ထုတ်မှု'
                })}
                {showFilters ? <ChevronUpIcon className="chevron" /> : <ChevronDownIcon className="chevron" />}
              </button>

              {/* Sort Dropdown */}
              <div className="sort-controls">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  {Object.entries(SORT_OPTIONS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {getText(label)}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="view-controls">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  title={getText({ en: 'Grid View', my: 'ဇယားကွက် မြင်ကွင်း' })}
                >
                  <AdjustmentsHorizontalIcon className="view-icon" />
                </button>
              </div>
            </div>
          </div>

          {/* ============================================================================
              EXPANDABLE FILTERS PANEL
              ============================================================================ */}
          
          {showFilters && (
            <div className="filters-panel">
              <div className="filters-content">
                
                {/* Category Filters */}
                <div className="filter-group">
                  <h4 className="filter-title">
                    {getText({
                      en: 'Categories',
                      my: 'အမျိုးအစားများ'
                    })}
                  </h4>
                  <div className="category-filters">
                    {/* All Categories Button */}
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                    >
                      {getText({
                        en: 'All Categories',
                        my: 'အမျိုးအစားအားလုံး'
                      })} ({categoryStats.all})
                    </button>
                    
                    {/* Individual Category Buttons */}
                    {Object.entries(CATEGORIES).map(([key, category]) => {
                      const IconComponent = category.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setSelectedCategory(key)}
                          className={`category-btn ${selectedCategory === key ? 'active' : ''}`}
                        >
                          <IconComponent className={`category-icon ${category.color}`} />
                          <span className="category-text">
                            {getText(category)} ({categoryStats[key] || 0})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Difficulty Filters */}
                <div className="filter-group">
                  <h4 className="filter-title">
                    {getText({
                      en: 'Difficulty Level',
                      my: 'အခက်အခဲ အဆင့်'
                    })}
                  </h4>
                  <div className="difficulty-filters">
                    {/* All Levels Button */}
                    <button
                      onClick={() => setSelectedDifficulty('all')}
                      className={`difficulty-btn ${selectedDifficulty === 'all' ? 'active' : ''}`}
                    >
                      {getText({
                        en: 'All Levels',
                        my: 'အဆင့်အားလုံး'
                      })}
                    </button>
                    
                    {/* Individual Difficulty Buttons */}
                    {Object.entries(DIFFICULTY_LEVELS).map(([key, level]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedDifficulty(key)}
                        className={`difficulty-btn ${selectedDifficulty === key ? 'active' : ''}`}
                      >
                        {getText(level)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Clear Filters Action */}
                <div className="filter-actions">
                  <button
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    {getText({
                      en: 'Clear All Filters',
                      my: 'စစ်ထုတ်မှုများ အားလုံး ရှင်းလင်းရန်'
                    })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================================
              RESULTS SUMMARY & ACTIVE FILTERS
              ============================================================================ */}
          
          <div className="results-summary">
            <div className="results-info">
              {/* Results Count */}
              <p className="results-text">
                {getText({
                  en: `Showing ${paginatedResources.length} of ${processedResources.length} resources`,
                  my: `စုစုပေါင်း ${processedResources.length} ခုမှ ${paginatedResources.length} ခု ပြသနေသည်`
                })}
              </p>
              
              {/* Active Filter Tags */}
              {(selectedCategory !== 'all' || selectedDifficulty !== 'all' || searchQuery) && (
                <div className="active-filters">
                  {/* Category Filter Tag */}
                  {selectedCategory !== 'all' && (
                    <span className="filter-tag">
                      {getText(CATEGORIES[selectedCategory])}
                      <button onClick={() => setSelectedCategory('all')}>
                        <XMarkIcon className="remove-filter" />
                      </button>
                    </span>
                  )}
                  
                  {/* Difficulty Filter Tag */}
                  {selectedDifficulty !== 'all' && (
                    <span className="filter-tag">
                      {getText(DIFFICULTY_LEVELS[selectedDifficulty])}
                      <button onClick={() => setSelectedDifficulty('all')}>
                        <XMarkIcon className="remove-filter" />
                      </button>
                    </span>
                  )}
                  
                  {/* Search Query Tag */}
                  {searchQuery && (
                    <span className="filter-tag">
                      "{searchQuery}"
                      <button onClick={() => setSearchQuery('')}>
                        <XMarkIcon className="remove-filter" />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ============================================================================
              LOADING STATE
              ============================================================================ */}
          
          {isLoading && (
            <div className="loading-state">
              <div className="loading-spinner">
                <div className="spinner-ring"></div>
              </div>
              <p className="loading-text">
                {getText({
                  en: 'Loading resources...',
                  my: 'အရင်းအမြစ်များ ဖွင့်နေသည်...'
                })}
              </p>
            </div>
          )}

          {/* ============================================================================
              ERROR STATE
              ============================================================================ */}
          
          {error && !isLoading && (
            <div className="error-state">
              <div className="error-content">
                <XMarkIcon className="error-icon" />
                <h3 className="error-title">
                  {getText({
                    en: 'Error Loading Resources',
                    my: 'အရင်းအမြစ်များ ဖွင့်ရာတွင် အမှားအယွင်း'
                  })}
                </h3>
                <p className="error-message">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                >
                  {getText({
                    en: 'Retry',
                    my: 'ထပ်မံကြိုးစားရန်'
                  })}
                </button>
              </div>
            </div>
          )}

          {/* ============================================================================
              RESOURCES GRID
              ============================================================================ */}
          
          {!isLoading && !error && (
            <div className={`resources-container ${viewMode}`}>
              {paginatedResources.map((resource) => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  onReadOnline={handleReadOnline}
                  onDownload={handleDownload}
                  categoryLabel={getText(CATEGORIES[resource.category])}
                  isLoading={isLoading}
                />
              ))}
            </div>
          )}

          {/* ============================================================================
              EMPTY STATE - No resources found
              ============================================================================ */}
          
          {!isLoading && !error && paginatedResources.length === 0 && (
            <div className="empty-state">
              <BookOpenIcon className="empty-icon" />
              <h3 className="empty-title">
                {getText({
                  en: 'No resources found',
                  my: 'အရင်းအမြစ်များ မတွေ့ရှိပါ'
                })}
              </h3>
              <p className="empty-description">
                {getText({
                  en: 'Try adjusting your search criteria or filters to find what you\'re looking for.',
                  my: 'သင်ရှာဖွေနေသည့်အရာကို ရှာတွေ့ရန် ရှာဖွေမှု စံနှုန်းများ သို့မဟုတ် စစ်ထုတ်မှုများကို ပြင်ဆင်ကြည့်ပါ။'
                })}
              </p>
              <button
                onClick={clearFilters}
                className="btn-primary"
              >
                {getText({
                  en: 'Clear Filters',
                  my: 'စစ်ထုတ်မှုများ ရှင်းလင်းရန်'
                })}
              </button>
            </div>
          )}

          {/* ============================================================================
              PAGINATION
              ============================================================================ */}
          
          {!isLoading && !error && totalPages > 1 && (
            <div className="pagination">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                {getText({
                  en: 'Previous',
                  my: 'ယခင်'
                })}
              </button>

              {/* Page Numbers */}
              <div className="pagination-numbers">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  // Calculate which page numbers to show (max 5 at a time)
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                {getText({
                  en: 'Next',
                  my: 'နောက်'
                })}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ============================================================================
          PDF READER MODAL
          Opens when user clicks "Read Online" button
          ============================================================================ */}
      
      <PDFReader
        resource={selectedResource}
        isOpen={showReader}
        onClose={() => setShowReader(false)}
      />
    </div>
  );
};

export default ReadingRoom;